import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShield, FiLock, FiMail, FiAlertCircle, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

// One-time nudge shown after login when the tenant has Two-Step Login
// enabled and the user hasn't been asked yet. Sends /profile/otp with
// prompted:true regardless of the user's answer so we only ask once.
export default function OtpFirstLoginPrompt() {
  const { user, authFetch, updateUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const shouldShow = !!user
    && user.otp_feature_enabled
    && !user.otp_prompted
    && !user.otp_enabled;
  const [open, setOpen] = useState(shouldShow);

  React.useEffect(() => { setOpen(shouldShow); }, [shouldShow]);

  if (!open) return null;

  const submit = async (enable) => {
    setBusy(true);
    try {
      const res = await authFetch('/api/users/profile/otp', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: enable, prompted: true }),
      });
      if (res.ok) {
        const data = await res.json();
        updateUser({ ...user, otp_enabled: !!data.otp_enabled, otp_prompted: true });
      } else {
        updateUser({ ...user, otp_prompted: true });
      }
    } catch {}
    setBusy(false);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 400 }}>
        <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 520, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
          <div style={{ padding: '22px 26px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiShield size={22} color="#2563eb" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>Turn on Two-Step Login?</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                  You'll get a short email code when you sign in — a lot harder to break into.
                </p>
              </div>
            </div>
            <button onClick={() => submit(false)} disabled={busy} title="Not now"
              style={{ background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer', color: 'var(--text-muted)', padding: 4 }}>
              <FiX size={20} />
            </button>
          </div>

          <div style={{ padding: '18px 26px' }}>
            <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
              {[
                { icon: FiLock, title: 'Password isn’t enough on its own', body: 'Even if someone learns your password, they can’t sign in without your inbox.' },
                { icon: FiMail, title: 'Nothing new to install', body: 'The 4-digit code just arrives in your email — no app, no phone-flip.' },
                { icon: FiAlertCircle, title: 'Brute-force protection built in', body: 'Three wrong codes locks the account for 60 minutes.' },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={13} color="#2563eb" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{b.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.5 }}>{b.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16, textAlign: 'center' }}>
              You can change this any time from Personal Settings.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => submit(false)} disabled={busy}
                style={{ flex: 1, padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                Not now
              </button>
              <button onClick={() => submit(true)} disabled={busy}
                style={{ flex: 1, padding: '11px 14px', borderRadius: 10, border: 'none', background: busy ? '#94a3b8' : '#2563eb', color: '#fff', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
                {busy ? 'Saving…' : 'Turn on Two-Step Login'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
